import React, { useMemo, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Container,
    Stack,
    TextField,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';

import PageHeader from '../core/PageHeader';
import { BrandedDataGridOverlay } from '../core/BrandedDataGridOverlay';
import { useActivityLog } from '../hooks/activityLog';
import { IActivityLogEntry } from '../Interfaces/IActivityLog';

const ENTITY_TYPE_LABELS: Record<string, string> = {
    event: 'Veranstaltungen',
    reservation: 'Reservierungen',
    storage_folder: 'Ordner',
    storage_file: 'Dateien',
    reservation_price: 'Übernachtungskosten',
    room: 'Zimmer',
    hut_book: 'Hüttenbuch',
};

const ACTION_LABELS: Record<string, string> = {
    created: 'Erstellt',
    updated: 'Bearbeitet',
    deleted: 'Gelöscht',
    favorited: 'Favorisiert',
    unfavorited: 'Entfavorisiert',
    comment_created: 'Kommentar',
    comment_updated: 'Kommentar bearbeitet',
    comment_deleted: 'Kommentar gelöscht',
    rsvp_updated: 'Rückmeldung',
};

const entityLabel = (value: string): string => ENTITY_TYPE_LABELS[value] ?? value;
const actionLabel = (value: string): string => ACTION_LABELS[value] ?? value;

const ActivityLogPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [search, setSearch] = useState('');
    const [entityTypeFilter, setEntityTypeFilter] = useState<string>('');

    const { data: entries = [], isLoading } = useActivityLog({ limit: 300 });

    const entityTypes = useMemo(
        () => Array.from(new Set(entries.map((entry) => entry.entity_type))).sort((a, b) => a.localeCompare(b, 'de')),
        [entries]
    );

    const filteredEntries = useMemo(() => {
        const term = search.trim().toLowerCase();
        return entries.filter((entry) => {
            if (entityTypeFilter && entry.entity_type !== entityTypeFilter) {
                return false;
            }
            if (!term) {
                return true;
            }
            return [
                entry.title,
                entry.description,
                entry.actor.displayName,
                entityLabel(entry.entity_type),
                actionLabel(entry.action),
            ]
                .join(' ')
                .toLowerCase()
                .includes(term);
        });
    }, [entries, entityTypeFilter, search]);

    const columns: GridColDef<IActivityLogEntry>[] = [
        {
            field: 'created_at',
            headerName: 'Datum',
            width: 170,
            renderCell: (params: GridRenderCellParams<IActivityLogEntry>) =>
                new Date(params.row.created_at).toLocaleString('de-DE'),
        },
        {
            field: 'entity_type',
            headerName: 'Bereich',
            width: 160,
            valueGetter: (_value, row) => entityLabel(row.entity_type),
            renderCell: (params: GridRenderCellParams<IActivityLogEntry>) => (
                <Chip size="small" label={entityLabel(params.row.entity_type)} />
            ),
        },
        ...(!isMobile
            ? [{
                field: 'action',
                headerName: 'Aktion',
                width: 160,
                valueGetter: (_value: unknown, row: IActivityLogEntry) => actionLabel(row.action),
            } as GridColDef<IActivityLogEntry>]
            : []),
        {
            field: 'title',
            headerName: 'Titel',
            flex: 1,
            minWidth: 220,
        },
        ...(!isMobile
            ? [{
                field: 'actor',
                headerName: 'Von',
                width: 170,
                sortable: false,
                valueGetter: (_value: unknown, row: IActivityLogEntry) => row.actor.displayName,
            } as GridColDef<IActivityLogEntry>]
            : []),
    ];

    return (
        <Box sx={{ py: { xs: 3, md: 4 }, width: '100%', maxWidth: '100%' }}>
            <Container maxWidth="xl">
                <PageHeader title="Aktivitätenlog" />

                <Stack spacing={2.5}>
                    <Card>
                        <CardContent>
                            <Stack spacing={1.5}>
                                <TextField
                                    label="Aktivität durchsuchen"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    size="small"
                                />
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Chip
                                        label="Alle Bereiche"
                                        size="small"
                                        color={entityTypeFilter === '' ? 'primary' : 'default'}
                                        onClick={() => setEntityTypeFilter('')}
                                    />
                                    {entityTypes.map((entityType) => (
                                        <Chip
                                            key={entityType}
                                            label={entityLabel(entityType)}
                                            size="small"
                                            color={entityTypeFilter === entityType ? 'primary' : 'default'}
                                            onClick={() => setEntityTypeFilter(entityType)}
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Box sx={{ height: { xs: 480, md: 600 }, width: '100%' }}>
                        <DataGrid
                            rows={filteredEntries}
                            columns={columns}
                            loading={isLoading}
                            disableRowSelectionOnClick
                            onRowClick={(params: GridRowParams<IActivityLogEntry>) => {
                                if (params.row.target_url) {
                                    navigate(params.row.target_url);
                                }
                            }}
                            getRowClassName={(params) => (params.row.target_url ? 'pvta-row-clickable' : '')}
                            pageSizeOptions={[25, 50, 100]}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25 } },
                                sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] },
                            }}
                            slots={{
                                noRowsOverlay: BrandedDataGridOverlay,
                                noResultsOverlay: BrandedDataGridOverlay,
                            }}
                            sx={{
                                '& .pvta-row-clickable': { cursor: 'pointer' },
                            }}
                        />
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
};

export default ActivityLogPage;
